import { SupabaseClient } from "@supabase/supabase-js";
import { signInWithGoogle } from "../../shared/supabase/supabase";
import { Database } from "../../shared/supabase/database.types";
import { retrySupabaseOnFail } from "../../shared/utils/retry-supabase";

export class AuthService {
  private static instance: AuthService;
  private supabaseClient: SupabaseClient<Database> | null = null;

  private constructor() {
    // OAuth callback will be handled directly in web app
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  async signInWithGoogle(supabase: SupabaseClient<Database>): Promise<void> {
    try {
      // Store the Supabase client for use in the callback
      this.supabaseClient = supabase;

      // For web app, we'll use the current window location as redirect URL
      const redirectUrl = `${window.location.origin}/auth/callback`;

      // Get the OAuth URL from Supabase
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
        }
      });

      if (error) {
        throw error;
      }

      // The OAuth flow will redirect back to our app
      // We don't need to handle it here as the router will catch it
    } catch (error) {
      console.error("Google sign in failed:", error);
      throw error;
    }
  }

  async signInWithApple(supabase: SupabaseClient<Database>): Promise<void> {
    try {
      // Store the Supabase client for use in the callback
      this.supabaseClient = supabase;

      // For web app, we'll use the current window location as redirect URL
      const redirectUrl = `${window.location.origin}/auth/callback`;

      // Get the OAuth URL from Supabase for Apple Sign In
      // Web requires the Services ID, not the App Bundle ID
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            client_id: 'com.writevoice.app.signin', // Services ID for web
          },
        }
      });

      if (error) {
        throw error;
      }

      // The OAuth flow will redirect back to our app
      // We don't need to handle it here as the router will catch it
    } catch (error) {
      console.error("Apple sign in failed:", error);
      throw error;
    }
  }

  async handleOAuthCallback(supabase: SupabaseClient<Database>): Promise<void> {
    try {
      // Get the hash from URL
      const hash = window.location.hash;
      
      if (hash) {

        // Parse the hash to get tokens
        const hashParams = new URLSearchParams(hash.substring(1));

        const access_token = hashParams.get("access_token");
        const refresh_token = hashParams.get("refresh_token");

        if (access_token && refresh_token) {
          // Set the session manually
          const { error } = await retrySupabaseOnFail(() => 
            supabase.auth.setSession({
              access_token,
              refresh_token,
            })
          );

          if (error) {
            console.error("Error setting session:", error);
          } else {
            // Force a session refresh to trigger auth state change
            const {
              data: { session: newSession },
            } = await retrySupabaseOnFail(() => supabase.auth.getSession());

            // Manually trigger auth state change if needed
            const {
              data: { user },
            } = await retrySupabaseOnFail(() => supabase.auth.getUser());
            if (user) {
              // Fire a custom event to notify components
              window.dispatchEvent(
                new CustomEvent("supabase-auth-success", {
                  detail: { session: newSession, user },
                })
              );
            }
          }
        }
      }
    } catch (error) {
      console.error("Error handling OAuth callback:", error);
    }
  }
}